import type { ImageDimensions } from '~/lib/image-dimensions';

/**
 * Hero photos are shown in Neo's Explore cards, about 370pt wide. Decode cost
 * is set by pixel count, not file size: the lookbook's 50 MP originals decode
 * to ~200 MB each and stall the app's UI thread mid-scroll (NEO-1580). So every
 * photo is shrunk before upload, and anything still over either limit is
 * flagged or refused.
 */
export const HERO_IMAGE_MAX_BYTES = 1_000_000;

/** 3× the card width, so photos stay sharp on 3× screens. */
export const HERO_IMAGE_MAX_WIDTH = 1200;

/** Keeps very tall images from slipping under the width cap. */
export const HERO_IMAGE_MAX_HEIGHT = 2400;

// A pixel budget rather than a width check: phones often store a portrait
// photo sideways with an EXIF rotate flag, so its header width isn't the width
// it displays at. Any photo `shrinkHeroImage` produces fits.
const HERO_IMAGE_MAX_PIXELS = HERO_IMAGE_MAX_WIDTH * HERO_IMAGE_MAX_HEIGHT;

const JPEG_QUALITIES = [0.8, 0.7, 0.6];

/** Decimal units, matching what Finder and Datadog RUM show. */
export function formatFileSize(bytes: number): string {
  if (bytes < 999_500) return `${Math.max(1, Math.round(bytes / 1000))} KB`;
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}

export function formatDimensions({ width, height }: ImageDimensions): string {
  return `${width}×${height}`;
}

export type HeroImageMeasurement = {
  bytes?: number | null;
  dimensions?: ImageDimensions | null;
};

/**
 * Videos are exempt: Explore plays them, it doesn't decode them as a bitmap.
 * A measurement that's missing a value can't trip that limit.
 */
export function isOversizedHeroImage(
  { bytes, dimensions }: HeroImageMeasurement,
  contentType: string | null
) {
  if (contentType?.startsWith('video/')) return false;
  if (bytes != null && bytes > HERO_IMAGE_MAX_BYTES) return true;
  // GIFs pass through shrinkHeroImage at full size, so only their byte size can be fixed.
  if (contentType === 'image/gif') return false;
  return dimensions != null && dimensions.width * dimensions.height > HERO_IMAGE_MAX_PIXELS;
}

/**
 * Server-side backstop for {@link shrinkHeroImage}: the browser shrinks every
 * photo first, so this only trips for a stale tab or a request made outside
 * hhOS.
 */
export const HERO_IMAGE_TOO_BIG_MESSAGE = `Photo is over ${formatFileSize(HERO_IMAGE_MAX_BYTES)} — reload the page so hhOS can shrink it, then try again`;

export class HeroImageShrinkError extends Error {}

// Animated GIFs would lose their animation and SVGs have no pixel size, so
// both pass through untouched (and are still subject to the size limit).
export function isShrinkable(file: File) {
  return (
    file.type.startsWith('image/') && file.type !== 'image/gif' && file.type !== 'image/svg+xml'
  );
}

export function encodeJpeg(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new HeroImageShrinkError('Could not encode photo'))),
      'image/jpeg',
      quality
    )
  );
}

export function jpegName(name: string) {
  const base = name.replace(/\.[^.]+$/, '') || 'photo';
  return `${base}.jpg`;
}

/**
 * Scales a photo down to fit {@link HERO_IMAGE_MAX_WIDTH} × {@link HERO_IMAGE_MAX_HEIGHT} and re-encodes it as
 * JPEG. Files that are already small and narrow, and videos, come back as-is.
 *
 * Throws {@link HeroImageShrinkError} when the photo can't be decoded (e.g. HEIC
 * in Chrome) and is too big to upload untouched, or when even the smallest
 * encoding is still over the limit.
 */
export async function shrinkHeroImage(file: File): Promise<File> {
  if (!isShrinkable(file)) {
    if (isOversizedHeroImage({ bytes: file.size }, file.type)) {
      throw new HeroImageShrinkError(
        `${file.name} is ${formatFileSize(file.size)} — export it as a JPEG under ${formatFileSize(HERO_IMAGE_MAX_BYTES)} and try again`
      );
    }
    return file;
  }

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    if (!isOversizedHeroImage({ bytes: file.size }, file.type)) return file;
    throw new HeroImageShrinkError(
      `Couldn't read ${file.name} to shrink it — export it as a JPEG and try again`
    );
  }

  try {
    const scale = Math.min(
      1,
      HERO_IMAGE_MAX_WIDTH / bitmap.width,
      HERO_IMAGE_MAX_HEIGHT / bitmap.height
    );
    if (scale === 1 && !isOversizedHeroImage({ bytes: file.size }, file.type)) return file;

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new HeroImageShrinkError('Could not shrink photo');
    // JPEG has no alpha; without a fill, transparent PNG areas turn black.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

    for (const quality of JPEG_QUALITIES) {
      const blob = await encodeJpeg(canvas, quality);
      if (blob.size <= HERO_IMAGE_MAX_BYTES) {
        return new File([blob], jpegName(file.name), {
          type: 'image/jpeg',
          lastModified: file.lastModified,
        });
      }
    }
    throw new HeroImageShrinkError(
      `${file.name} is still over ${formatFileSize(HERO_IMAGE_MAX_BYTES)} after shrinking — try a simpler photo`
    );
  } finally {
    bitmap.close();
  }
}
